import React, { useState, useEffect, useRef } from 'react';
import { remindersService } from '../../services/remindersService';
import './Reminders.css';

const PRESETS = [
  { label: '10 min',   key: '10m',      ms: 10  * 60000 },
  { label: '15 min',   key: '15m',      ms: 15  * 60000 },
  { label: '30 min',   key: '30m',      ms: 30  * 60000 },
  { label: '1 hour',   key: '1h',       ms: 60  * 60000 },
  { label: '2 hours',  key: '2h',       ms: 120 * 60000 },
  { label: '3 hours',  key: '3h',       ms: 180 * 60000 },
  { label: 'Tomorrow', key: 'tomorrow', ms: null },
  { label: 'Custom…',  key: 'custom',   ms: null },
];

const buildReminderTime = (preset, customDatetime) => {
  if (!preset) return null;
  if (preset === 'custom') return customDatetime ? new Date(customDatetime).toISOString() : null;
  if (preset === 'tomorrow') {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  const found = PRESETS.find(p => p.key === preset);
  return found ? new Date(Date.now() + found.ms).toISOString() : null;
};

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso), pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const buildEmptyForm = (defaultOrderNo, defaultTitle = '') => ({
  title: defaultTitle,
  notes: '',
  preset: null,
  customDatetime: '',
  assignedTo: '',
  orderQuery: defaultOrderNo ? `Order #${defaultOrderNo}` : '',
  linkedOrderNo: defaultOrderNo ? String(defaultOrderNo) : '',
  linkedOrderDisplay: defaultOrderNo ? `Order #${defaultOrderNo}` : '',
  customerQuery: '',
  linkedCustomerID: null,
  linkedCustomerName: '',
  showMore: !!defaultOrderNo,
});

export default function ReminderFormModal({
  username,
  onClose,
  onSaved,
  defaultOrderNo = null,
  defaultDatetime = null,
  defaultTitle = '',
  editingReminder = null,
  placeholder = 'What needs to be done?',
}) {
  const [form, setForm] = useState(() => {
    if (editingReminder) {
      return {
        title: editingReminder.Title,
        notes: editingReminder.Notes || '',
        preset: editingReminder.ReminderTime ? 'custom' : null,
        customDatetime: toLocalInputValue(editingReminder.ReminderTime),
        assignedTo: editingReminder.AssignedTo || '',
        orderQuery: editingReminder.LinkedOrderNo || '',
        linkedOrderNo: editingReminder.LinkedOrderNo || '',
        linkedOrderDisplay: editingReminder.LinkedOrderNo ? `#${editingReminder.LinkedOrderNo}` : '',
        customerQuery: editingReminder.LinkedCustomerName || '',
        linkedCustomerID: editingReminder.LinkedCustomerID || null,
        linkedCustomerName: editingReminder.LinkedCustomerName || '',
        showMore: !!(editingReminder.AssignedTo || editingReminder.LinkedOrderNo || editingReminder.LinkedCustomerName),
      };
    }
    const base = buildEmptyForm(defaultOrderNo, defaultTitle);
    if (defaultDatetime) {
      base.preset = 'custom';
      base.customDatetime = defaultDatetime;
    }
    return base;
  });

  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [orderResults, setOrderResults] = useState([]);
  const [customerResults, setCustomerResults] = useState([]);
  const orderTimerRef = useRef(null);
  const customerTimerRef = useRef(null);

  useEffect(() => {
    remindersService.getUsers().then(setUsers).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const reminderTime = buildReminderTime(form.preset, form.customDatetime);
      const extras = {
        assignedTo:         form.assignedTo || null,
        assignedBy:         form.assignedTo ? username : null,
        linkedOrderNo:      form.linkedOrderNo || null,
        linkedCustomerID:   form.linkedCustomerID || null,
        linkedCustomerName: form.linkedCustomerName || null,
      };
      let result;
      if (editingReminder) {
        result = await remindersService.update(editingReminder.ReminderID, username, {
          title: form.title, notes: form.notes || null, reminderTime, ...extras,
        });
      } else {
        result = await remindersService.create(username, form.title, form.notes || null, reminderTime, extras);
      }
      onSaved?.(result);
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleOrderQuery = (val) => {
    setForm(f => ({ ...f, orderQuery: val, linkedOrderNo: '', linkedOrderDisplay: '' }));
    clearTimeout(orderTimerRef.current);
    if (val.length < 2) { setOrderResults([]); return; }
    orderTimerRef.current = setTimeout(async () => {
      try { setOrderResults(await remindersService.searchOrders(val)); } catch (_) {}
    }, 300);
  };

  const selectOrder = (o) => {
    setForm(f => ({
      ...f,
      linkedOrderNo: o.OrderNo,
      linkedOrderDisplay: `#${o.OrderNo} — ${o.CustomerName || ''}`,
      orderQuery: `#${o.OrderNo} — ${o.CustomerName || ''}`,
    }));
    setOrderResults([]);
  };

  const handleCustomerQuery = (val) => {
    setForm(f => ({ ...f, customerQuery: val, linkedCustomerID: null, linkedCustomerName: '' }));
    clearTimeout(customerTimerRef.current);
    if (val.length < 2) { setCustomerResults([]); return; }
    customerTimerRef.current = setTimeout(async () => {
      try { setCustomerResults(await remindersService.searchCustomers(val)); } catch (_) {}
    }, 300);
  };

  const selectCustomer = (c) => {
    const name = c.CustomerFullName || `${c.CustomerFirstName} ${c.CustomerLastName}`;
    setForm(f => ({ ...f, linkedCustomerID: c.ID, linkedCustomerName: name, customerQuery: name }));
    setCustomerResults([]);
  };

  const otherUsers = users.filter(u => u.UserName !== username);

  return (
    <div className="reminder-form-overlay" onClick={onClose}>
      <div className="reminder-form-card" onClick={e => e.stopPropagation()}>
        <h3>{editingReminder ? 'Edit Alert' : 'New Alert'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              autoFocus
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder={placeholder}
              maxLength={200}
              required
            />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Optional details..."
              rows={2}
              maxLength={500}
            />
          </div>

          <div className="form-group">
            <label><i className="fas fa-clock"></i> Remind me in</label>
            <div className="preset-grid">
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  type="button"
                  className={`preset-btn ${form.preset === p.key ? 'selected' : ''}`}
                  onClick={() => setForm(f => ({ ...f, preset: f.preset === p.key ? null : p.key }))}
                >
                  {p.label}
                </button>
              ))}
              {form.preset && (
                <button
                  type="button"
                  className="preset-btn preset-clear"
                  onClick={() => setForm(f => ({ ...f, preset: null, customDatetime: '' }))}
                >
                  <i className="fas fa-times"></i> No reminder
                </button>
              )}
            </div>
            {form.preset === 'custom' && (
              <input
                className="custom-datetime-input"
                type="datetime-local"
                value={form.customDatetime}
                onChange={e => setForm(f => ({ ...f, customDatetime: e.target.value }))}
              />
            )}
            {form.preset && form.preset !== 'custom' && (
              <p className="form-hint sms-note"><i className="fas fa-sms"></i> Pop-up + SMS will fire when the time arrives.</p>
            )}
            {form.preset === 'custom' && form.customDatetime && (
              <p className="form-hint sms-note"><i className="fas fa-sms"></i> Pop-up + SMS will fire when the time arrives.</p>
            )}
            {!form.preset && <p className="form-hint">Leave unset to save as a plain to-do.</p>}
          </div>

          <button
            type="button"
            className="more-options-toggle"
            onClick={() => setForm(f => ({ ...f, showMore: !f.showMore }))}
          >
            <i className={`fas fa-chevron-${form.showMore ? 'up' : 'down'}`}></i>
            {form.showMore ? 'Fewer options' : 'More options (assign, link order/customer)'}
          </button>

          {form.showMore && (
            <div className="more-options-body">
              <div className="form-group">
                <label><i className="fas fa-user-plus"></i> Assign to</label>
                <select
                  value={form.assignedTo}
                  onChange={e => setForm(f => ({ ...f, assignedTo: e.target.value }))}
                  className="form-select"
                >
                  <option value="">— No assignment (just me) —</option>
                  {otherUsers.map(u => (
                    <option key={u.UserName} value={u.UserName}>
                      {u.UserFullName || `${u.UserFirstName} ${u.UserLastName}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group search-field-group">
                <label><i className="fas fa-file-alt"></i> Link to Order</label>
                <input
                  type="text"
                  value={form.orderQuery}
                  onChange={e => handleOrderQuery(e.target.value)}
                  placeholder="Type order # or customer name…"
                  className={form.linkedOrderNo ? 'linked' : ''}
                />
                {form.linkedOrderNo && (
                  <span className="linked-badge">
                    <i className="fas fa-check"></i> {form.linkedOrderDisplay}
                    <button
                      type="button"
                      className="unlink-btn"
                      onClick={() => setForm(f => ({ ...f, linkedOrderNo: '', linkedOrderDisplay: '', orderQuery: '' }))}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </span>
                )}
                {orderResults.length > 0 && (
                  <ul className="search-dropdown">
                    {orderResults.map(o => (
                      <li key={o.OrderNo} onClick={() => selectOrder(o)}>
                        <strong>#{o.OrderNo}</strong> {o.OrderType && `· ${o.OrderType}`} {o.CustomerName && `· ${o.CustomerName}`}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="form-group search-field-group">
                <label><i className="fas fa-user"></i> Link to Customer</label>
                <input
                  type="text"
                  value={form.customerQuery}
                  onChange={e => handleCustomerQuery(e.target.value)}
                  placeholder="Type customer name or phone…"
                  className={form.linkedCustomerID ? 'linked' : ''}
                />
                {form.linkedCustomerID && (
                  <span className="linked-badge">
                    <i className="fas fa-check"></i> {form.linkedCustomerName}
                    <button
                      type="button"
                      className="unlink-btn"
                      onClick={() => setForm(f => ({ ...f, linkedCustomerID: null, linkedCustomerName: '', customerQuery: '' }))}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </span>
                )}
                {customerResults.length > 0 && (
                  <ul className="search-dropdown">
                    {customerResults.map(c => (
                      <li key={c.ID} onClick={() => selectCustomer(c)}>
                        {c.CustomerFullName || `${c.CustomerFirstName} ${c.CustomerLastName}`}
                        {c.CustomerPhone && <span className="search-sub"> · {c.CustomerPhone}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving...' : (editingReminder ? 'Save Changes' : 'Add Alert')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
